import { defineStore } from "pinia";
import { ref, computed } from "vue";

import authService from "@/services/authService";
import { forgetListReturn } from "@/composables/useListReturn";

const userStorageKey = "user-auth-info";
const activeCompanyStorageKey = "activeCompanyId";

// Decode the `user_id` claim from a simplejwt access token (base64url payload).
function pkFromAccessToken(access) {
  try {
    let b64 = access.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    b64 += "=".repeat((4 - (b64.length % 4)) % 4);
    return JSON.parse(atob(b64)).user_id ?? null;
  } catch {
    return null;
  }
}

export const useAuthStore = defineStore("auth", () => {
  const storedStr = localStorage.getItem(userStorageKey);
  const userInfo = ref(storedStr ? JSON.parse(storedStr) : {});
  // Legacy sessions predate `pk` in the login payload; backfill it from the JWT so
  // identity checks (e.g. isSelf on the admin page) never misclassify the current user.
  if (userInfo.value.access && userInfo.value.pk == null) {
    const pk = pkFromAccessToken(userInfo.value.access);
    if (pk != null) userInfo.value.pk = pk;
  }
  const loggedIn = computed(
    () =>
      (userInfo.value.access && userInfo.value.secondStepCompleted) ||
      (userInfo.value.access && userInfo.value.secondStepNotRequired)
  );

  const activeCompanyId = ref(sessionStorage.getItem(activeCompanyStorageKey) || null);

  function setActiveCompany(id) {
    forgetListReturn();
    if (id == null) {
      sessionStorage.removeItem(activeCompanyStorageKey);
      activeCompanyId.value = null;
    } else {
      sessionStorage.setItem(activeCompanyStorageKey, String(id));
      activeCompanyId.value = String(id);
    }
  }

  function setNewUserInfo(newUserInfo) {
    localStorage.setItem(userStorageKey, JSON.stringify(newUserInfo));
    userInfo.value = newUserInfo;
  }

  function setNewAccessToken(newAccessToken) {
    setNewUserInfo({ ...userInfo.value, access: newAccessToken });
  }

  function applySignInPayload(payload) {
    const {
      firstName,
      lastName,
      access,
      pk,
      isAdmin,
      canSwitch,
      assignedCasesOnly,
      canViewInsights,
      isProvider,
      isProviderOfficeStaff,
      canViewProviderInsights,
    } = payload;

    // Only after a successful identity transition: a returning tenant may have a persisted
    // X-Active-Company, and the new identity (a provider has no permitted company at all) must not
    // inherit it. Clearing before the await would strand a still-valid session on a failed attempt.
    setActiveCompany(null);

    setNewUserInfo({
      firstName,
      lastName,
      access,
      pk,
      isAdmin,
      canSwitch,
      assignedCasesOnly,
      canViewInsights,
      isProvider,
      isProviderOfficeStaff,
      canViewProviderInsights,
    });
  }

  async function login(creds) {
    const payload = await authService.login(creds);
    applySignInPayload(payload);
    return { otpAppUrl: payload.otpAppUrl, setupKey: payload.setupKey, secondStep: payload.secondStep };
  }

  async function loginSso(code) {
    const payload = await authService.exchangeSsoCode(code);
    applySignInPayload(payload);
    return { secondStep: payload.secondStep };
  }

  async function loginStep2(code) {
    await authService.loginStep2(code);

    setNewUserInfo({ ...userInfo.value, secondStepCompleted: true });
  }

  async function resendOtp() {
    await authService.resendOtp();
  }

  async function loginStep2NotRequired() {
    setNewUserInfo({ ...userInfo.value, secondStepNotRequired: true });
  }

  function handleSessionExpired() {
    clearUserInfo();
    const { pathname, search } = window.location;
    const redirect = pathname !== "/login" ? `?redirect=${encodeURIComponent(pathname + search)}` : "";
    window.location.href = `/login${redirect}`;
  }

  async function logout() {
    try {
      await authService.logout();
    } catch (error) {
      // if request failed with 401 or 403 user is already logged out and we can ignore it
      if (![401, 403].includes(error.response.status)) {
        // if it's an other problem, user is probably still logged in(refresh token is still valid)
        alert("Logout failed!");
        throw error;
      }
    }

    clearUserInfo();
  }

  function clearUserInfo() {
    // logout successful. forget token
    localStorage.removeItem(userStorageKey);
    userInfo.value = {};
    setActiveCompany(null);
  }

  return {
    login,
    loginSso,
    logout,
    handleSessionExpired,
    userInfo,
    loggedIn,
    loginStep2,
    resendOtp,
    loginStep2NotRequired,
    setNewAccessToken,
    activeCompanyId,
    setActiveCompany,
  };
});
